import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import { IconButton } from './Button';
import { IconClose } from './icons';
import { useKit } from './strings';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';
export type ToastInput = { title: string; description?: string; tone?: ToastTone; action?: { label: string; onClick: () => void }; duration?: number };
export type ToastItem = ToastInput & { id: string; tone: ToastTone; duration: number };

type Action = { type: 'add'; toast: ToastItem } | { type: 'remove'; id: string };

/** At most MAX toasts on screen; the oldest leaves first. Danger toasts stay until dismissed. */
export const MAX_TOASTS = 3;
export function toastReducer(state: ToastItem[], a: Action): ToastItem[] {
  if (a.type === 'remove') return state.filter((t) => t.id !== a.id);
  return [...state.filter((t) => t.id !== a.toast.id), a.toast].slice(-MAX_TOASTS);
}
export const defaultDuration = (tone: ToastTone) => (tone === 'danger' ? 0 : tone === 'warning' ? 8000 : 5000);

type ToastApi = { toast: (t: ToastInput) => string; dismiss: (id: string) => void };
const Ctx = createContext<ToastApi | null>(null);

let seq = 0;

/** Mount once per surface. Renders a polite live region at the inline-end bottom corner. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, dispatch] = useReducer(toastReducer, []);
  const dismiss = useCallback((id: string) => dispatch({ type: 'remove', id }), []);
  const toast = useCallback((t: ToastInput) => {
    const tone = t.tone ?? 'info';
    const id = `qkt${++seq}`;
    dispatch({ type: 'add', toast: { ...t, id, tone, duration: t.duration ?? defaultDuration(tone) } });
    return id;
  }, []);
  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
  const { strings } = useKit();
  return (
    <Ctx.Provider value={api}>
      {children}
      <section aria-label={strings.notifications} className="qk">
        <ol className="qk-toasts" aria-live="polite" aria-relevant="additions">
          {items.map((t) => <Toast key={t.id} item={t} onDismiss={() => dismiss(t.id)} dismissLabel={strings.dismiss} />)}
        </ol>
      </section>
    </Ctx.Provider>
  );
}

function Toast({ item, onDismiss, dismissLabel }: { item: ToastItem; onDismiss: () => void; dismissLabel: string }) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // the latest callback, so a parent re-render never restarts the countdown
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  const start = useCallback(() => {
    clearTimeout(timer.current);
    if (item.duration > 0) timer.current = setTimeout(() => dismissRef.current(), item.duration);
  }, [item.duration]);
  const stop = () => clearTimeout(timer.current);
  useEffect(() => { start(); return () => clearTimeout(timer.current); }, [start]);
  return (
    <li className="qk-toast" data-tone={item.tone} role={item.tone === 'danger' ? 'alert' : undefined} onMouseEnter={stop} onMouseLeave={start} onFocus={stop} onBlur={start}>
      <span className="qk-toast__dot" aria-hidden="true" />
      <div className="qk-toast__body">
        <span className="qk-toast__title">{item.title}</span>
        {item.description ? <span className="qk-toast__desc">{item.description}</span> : null}
      </div>
      {item.action ? (
        <button type="button" className="qk-btn qk-toast__action" data-variant="ghost" data-size="sm" onClick={() => { item.action!.onClick(); onDismiss(); }}>
          {item.action.label}
        </button>
      ) : null}
      <IconButton label={dismissLabel} size="sm" onClick={onDismiss}><IconClose /></IconButton>
    </li>
  );
}

/** toast({ title, tone }) from anywhere under ToastProvider. */
export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
